/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Accordion,
    AccordionContent,
    AccordionHeading,
    AccordionItem,
    Button,
    ButtonGroup,
    FlexCol,
    FlexRow,
    Icon,
    Stack,
    Text,
    useFlags,
} from 'sarsaparilla';
import { SITE_PASS_BANNERS } from '../constants';
import { showSitePassBanner } from '../utils/launchDarklyUtils';

function SitePassBanner({ facilityId = '', isInWidget = false, baseDomain = '' }) {
    const [sitePassBannerState, setSitePassBannerState] = useState({ activeIndex: 0 });
    const { facilitiesUsingSitePassIntegration } = useFlags();
    const sitePassBannerData = useMemo(() => {
        return SITE_PASS_BANNERS?.[facilityId] ?? null;
    }, [facilityId]);

    // launch darkly flags does not work for the widget
    const shouldShowSitePassBanner = useMemo(() => {
        return isInWidget
            ? false
            : showSitePassBanner(facilityId, facilitiesUsingSitePassIntegration);
    }, [facilitiesUsingSitePassIntegration, facilityId, isInWidget]);

    const getBookReservationUrl = useCallback(() => {
        let baseUrl = `/timed-entry/${sitePassBannerData?.timedEntryFacilityId}`;

        if (baseDomain) {
            baseUrl = `${baseDomain}${baseUrl}`;
        }

        return baseUrl;
    }, [baseDomain, sitePassBannerData?.timedEntryFacilityId]);

    const handleBookReservation = useCallback(() => {
        window.location.href = getBookReservationUrl();
    }, [getBookReservationUrl]);

    const handleToggleAccordion = useCallback((index) => {
        setSitePassBannerState((prevState) => {
            const activeIndex =
                prevState?.activeIndex || prevState?.activeIndex === 0 ? null : index;
            return { ...prevState, activeIndex };
        });
    }, []);

    const renderAreasWithReservation = () => {
        if (sitePassBannerData?.areasWithReservation?.length) {
            return (
                <FlexRow>
                    <FlexCol md="variable">
                        <Text
                            id="site-pass-banner-list-title"
                            className="site-pass-banner__list-title mb-1 mb-md-0"
                            color="white"
                            aria-hidden="true"
                        >
                            {sitePassBannerData?.areaListTitle ||
                                'Areas that require a reservation:'}
                        </Text>
                    </FlexCol>
                    <FlexCol>
                        <ul
                            className="site-pass-banner__list"
                            aria-labelledby="site-pass-banner-list-title"
                        >
                            {sitePassBannerData?.areasWithReservation?.map(
                                (areaWithReservation, key) => (
                                    <li
                                        className="site-pass-banner__list-item"
                                        key={key}
                                        aria-label={`${areaWithReservation?.name} ${areaWithReservation?.dates}`}
                                    >
                                        <div
                                            className="site-pass-banner__list-content"
                                            aria-hidden="true"
                                        >
                                            <Text
                                                id="site-pass-banner__list-name"
                                                className="site-pass-banner__list-name"
                                                color="white"
                                                tag="span"
                                            >
                                                {areaWithReservation?.name}
                                            </Text>
                                            {areaWithReservation?.dates && (
                                                <>
                                                    <Icon
                                                        iconName="calendar"
                                                        className="site-pass-banner__list-date-icon"
                                                        size="sm"
                                                    />
                                                    <Text
                                                        id="site-pass-banner__list-date-text"
                                                        className="site-pass-banner__list-date-text"
                                                        color="white"
                                                        fontStyle="italic"
                                                        tag="span"
                                                        size="sm"
                                                    >
                                                        {areaWithReservation?.dates}
                                                    </Text>
                                                </>
                                            )}
                                        </div>
                                    </li>
                                )
                            )}
                        </ul>
                    </FlexCol>
                </FlexRow>
            );
        }

        return null;
    };

    const renderButtons = () => {
        if (sitePassBannerData?.hideBookReservationButton) {
            return null;
        }

        if (isInWidget) {
            return (
                <ButtonGroup className="site-pass-banner__buttons-container mt-2 mt-sm-0">
                    <Button
                        appearance="tertiary-white"
                        clickTagAction={`Location ID - ${facilityId}`}
                        clickTagCategory="Site Pass"
                        clickTagLabel="Pass Upsell Promo - Book Reservation Now Button"
                        iconBeforeElement={
                            <Icon
                                iconName="car"
                                className="site-pass-banner__button-icon"
                            />
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        href={getBookReservationUrl()}
                        gaTrackingId="775753010796"
                    >
                        Book Reservation Now
                    </Button>
                </ButtonGroup>
            );
        }

        return (
            <ButtonGroup className="site-pass-banner__buttons-container mt-2 mt-sm-0">
                <Button
                    appearance="tertiary-white"
                    clickTagAction={`Location ID - ${facilityId}`}
                    clickTagCategory="Site Pass"
                    clickTagLabel="Pass Upsell Promo - Book Reservation Now Button"
                    iconBeforeElement={
                        <Icon iconName="car" className="site-pass-banner__button-icon" />
                    }
                    onClick={handleBookReservation}
                    gaTrackingId="637104568639"
                >
                    Book Reservation Now
                </Button>
            </ButtonGroup>
        );
    };

    if (!sitePassBannerData || !sitePassBannerData?.title || !shouldShowSitePassBanner) {
        return null;
    }

    return (
        <div className="site-pass-banner">
            <div className="site-pass-banner__img-background" />
            <Accordion
                className="site-pass-banner__accordion"
                headingAppearance="h5"
                activeIndex={sitePassBannerState?.activeIndex}
                onChange={handleToggleAccordion}
            >
                <AccordionItem>
                    <AccordionHeading
                        className="site-pass-banner__accordion-heading"
                        headingLevel={2}
                    >
                        {sitePassBannerData?.title}
                    </AccordionHeading>
                    <AccordionContent className="site-pass-banner__accordion-content">
                        <Stack space="sm">
                            {sitePassBannerData?.description && (
                                <Text
                                    className="site-pass-banner__description "
                                    color="white"
                                    fontWeight="semibold"
                                >
                                    {sitePassBannerData?.description}
                                </Text>
                            )}
                            {(sitePassBannerData?.areasWithReservation?.length ||
                                !sitePassBannerData?.hideBookReservationButton) && (
                                <FlexRow alignItems="end">
                                    <FlexCol>{renderAreasWithReservation()}</FlexCol>
                                    <FlexCol sm="variable">{renderButtons()}</FlexCol>
                                </FlexRow>
                            )}
                        </Stack>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
}

SitePassBanner.propTypes = {
    facilityId: PropTypes.string,
    isInWidget: PropTypes.bool,
    baseDomain: PropTypes.string,
};

export default SitePassBanner;
